'use client'

import React, { useEffect, useState, useRef } from 'react'
import dynamic from 'next/dynamic'
import { motion, AnimatePresence } from 'framer-motion'
import { Terminal, Activity, Beaker, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react'
import type { InventoryItem } from '@/lib/api/silo-api/types'

// ✅ OPTIMIZED: Dynamic imports for code splitting (reduces initial bundle by ~44 KiB)
const CannabinoidProfileChart = dynamic(
  () => import('./CannabinoidProfileChart').then(mod => mod.CannabinoidProfileChart),
  { ssr: false, loading: () => <div className="h-full flex items-center justify-center text-white/40">Loading chart...</div> }
)

const TerpeneProfileChart = dynamic(
  () => import('./TerpeneProfileChart').then(mod => mod.TerpeneProfileChart),
  { ssr: false, loading: () => <div className="h-full flex items-center justify-center text-white/40">Loading chart...</div> }
)

const LabResultsCard = dynamic(
  () => import('./LabResultsCard').then(mod => mod.LabResultsCard),
  { ssr: false, loading: () => <div className="h-full flex items-center justify-center text-white/40">Loading results...</div> }
)

interface InventoryTerminalDisplayProps {
  selectedProduct: InventoryItem | null
  onAddProduct?: (product: InventoryItem) => void
}

export default function InventoryTerminalDisplay({ selectedProduct, onAddProduct }: InventoryTerminalDisplayProps) {
  const [currentView, setCurrentView] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const pauseTimeoutRef = useRef<NodeJS.Timeout>()
  
  // Rotate through views every 8 seconds (slower), but pause on interaction
  useEffect(() => {
    if (!selectedProduct || isPaused) return
    
    const interval = setInterval(() => {
      setCurrentView((prev) => (prev + 1) % 3)
    }, 8000) // Increased from 4000 to 8000
    
    return () => clearInterval(interval)
  }, [selectedProduct, isPaused])

  // Reset view when product changes
  useEffect(() => {
    setCurrentView(0)
    setIsPaused(false)
    // Debug: Log product data to see what we're getting
    if (selectedProduct) {
      console.log('Selected product changed:', {
        id: selectedProduct.id,
        name: selectedProduct.name,
        cannabinoids: selectedProduct.cannabinoids,
        terpenes: selectedProduct.terpenes,
        hasData: !!(selectedProduct.cannabinoids?.length || selectedProduct.terpenes?.length)
      })
    }
  }, [selectedProduct])
  
  // Handle manual tab selection
  const handleTabClick = (index: number) => {
    setCurrentView(index)
    setIsPaused(true)
    
    // Clear existing timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current)
    }
    
    // Resume auto-cycling after 15 seconds of inactivity
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false)
    }, 15000)
  }

  if (!selectedProduct) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Terminal className="h-12 w-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">Select a product to view details</p>
        </div>
      </div>
    )
  }

  const views = [
    {
      title: 'Cannabinoid Profile',
      icon: <Activity className="h-4 w-4" />,
      component: CannabinoidProfileChart
    },
    {
      title: 'Terpene Analysis',
      icon: <Beaker className="h-4 w-4" />,
      component: TerpeneProfileChart
    },
    {
      title: 'Lab Results',
      icon: <ShieldCheck className="h-4 w-4" />,
      component: LabResultsCard
    }
  ]

  const handlePrevView = () => {
    setCurrentView((prev) => (prev - 1 + 3) % 3)
    setIsPaused(true)
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 15000)
  }

  const handleNextView = () => {
    setCurrentView((prev) => (prev + 1) % 3)
    setIsPaused(true)
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 15000)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Terminal Header with Navigation */}
      <div className="glass-dense p-3 rounded-t-xl border border-white/10">
        <div className="flex items-center justify-between">
          {/* Left Arrow */}
          <button
            onClick={handlePrevView}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Previous view"
          >
            <ChevronLeft className="h-5 w-5 text-white/60 hover:text-white" />
          </button>

          {/* Dots Navigation */}
          <div className="flex gap-2">
            {views.map((view, index) => (
              <button
                key={index}
                onClick={() => handleTabClick(index)}
                className={`transition-all duration-200 ${
                  currentView === index
                    ? 'w-3 h-3 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.6)]'
                    : 'w-2 h-2 rounded-full bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`View ${view.title}`}
              />
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={handleNextView}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Next view"
          >
            <ChevronRight className="h-5 w-5 text-white/60 hover:text-white" />
          </button>
        </div>
      </div>

      {/* Product Info Bar */}
      <div className="glass-dense p-3 border-x border-white/10">
        <h3 className="text-sm font-semibold text-white mb-1">{selectedProduct.name}</h3>
        <div className="flex items-center gap-3 text-xs text-white/60">
          <span>Type: {selectedProduct.product_type?.name || 'Unknown'}</span>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex border-x border-white/10">
        {views.map((view, index) => (
          <button
            key={index}
            onClick={() => handleTabClick(index)}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              currentView === index
                ? 'bg-purple-500/20 text-purple-300 border-b-2 border-purple-500'
                : 'bg-gray-800/30 text-white/60 hover:bg-white/5 border-b border-white/10'
            }`}
          >
            {view.icon}
            <span className="hidden sm:inline">{view.title}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 glass-dense rounded-b-xl border border-t-0 border-white/10 p-4 overflow-y-auto min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentView}-${selectedProduct.id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            {currentView === 0 && <CannabinoidProfileChart product={selectedProduct} />}
            {currentView === 1 && <TerpeneProfileChart product={selectedProduct} />}
            {currentView === 2 && <LabResultsCard product={selectedProduct} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}